<script setup>
import { ref, watch, computed } from 'vue';
import axios from 'axios';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { clone } from 'lodash';
import { transporte } from 'cifra-api-js';
import SelectorAsiento from './SelectorAsiento.vue';
import InputError from '../../../components/cifra/InputError.vue';
import Overlay from '../../../libs/cifra/overlay';
import { dateToLocaleDateString } from '../../../libs/dates';
import { getModelJsonErrors } from '../../../libs/cifra/model_errors';
import InputDiasSemana from '../../../components/cifra/InputDiasSemana.vue';

const TIPOS_VARIACIONES = [
  { id: 'ausencia', nombre: 'No asiste' },
  { id: 'cambio_parada', nombre: 'Cambio de parada' },
  { id: 'cambio_turno', nombre: 'Cambio de turno' },
];

const props = defineProps({
  turnoId: { type: Number, required: true },
  inscripcionId: { type: Number, default: undefined },
  actionUrl: { type: String, default: () => (document.location.toString()) },
  tiposPermitidos: { type: Array, default: undefined },
  diasSemana: { type: Array, default: () => [] },
});

const tipos = ref([...TIPOS_VARIACIONES]);
if (props.tiposPermitidos) {
  tipos.value = tipos.value.filter((tipo) => props.tiposPermitidos.includes(tipo.id));
}
const variacion = ref({ tipo: 'ausencia' });
if (props.inscripcionId) {
  variacion.value.inscripcion_id = props.inscripcionId;
}

const errores = ref({});
const range = ref({});
const rutas = ref([]);
const turnos = ref([]);
const paradas = ref([]);
const rutaId = ref();
const guardando = ref(false);
const formVariacion = ref();

const seleccionarDias = ref(false);
// Días de la semana seleccionados en el cambio de parada
const diasSemanaSeleccionados = ref(props.diasSemana);

const fechaInicio = computed(() => {
  if (!variacion.value.fecha_inicio) {
    return undefined;
  }
  return dateToLocaleDateString(new Date(variacion.value.fecha_inicio));
});

const fechaFin = computed(() => {
  if (!variacion.value.fecha_fin) {
    return undefined;
  }
  return dateToLocaleDateString(new Date(variacion.value.fecha_fin));
});

const puedeGuardar = computed(() => {
  if (!fechaInicio.value || !fechaFin.value) {
    return false;
  }
  if (variacion.value.tipo !== 'ausencia' && !variacion.value.parada_id) {
    return false;
  }
  return true;
});

const masDeUnaSemana = computed(() => {
  if (!variacion.value.fecha_inicio || !variacion.value.fecha_fin) {
    return false;
  }

  const dias = differenceInCalendarDays(
    parseISO(variacion.value.fecha_fin),
    parseISO(variacion.value.fecha_inicio),
  );

  // La diferencia de días siempre será uno menos de los días implicados por eso el +1
  return (dias + 1) >= 7;
});

function cargarParadas(turnoId) {
  transporte.paradas.get({ params: { turno_id: turnoId } }).then((result) => {
    paradas.value = result.data;
  });
}
function guardar() {
  guardando.value = true;
  const overlay = new Overlay(formVariacion.value);

  overlay.show();
  if (variacion.value.tipo === 'cambio_parada' && masDeUnaSemana.value && seleccionarDias) {
    variacion.value.dias_semana = diasSemanaSeleccionados.value;
  } else {
    variacion.value.dias_semana = undefined;
  }

  axios.post(props.actionUrl, { variacion: variacion.value }).then(() => {
  }).catch((e) => {
    errores.value = getModelJsonErrors(e.response.data);
  }).then(() => {
    overlay.hide();
    guardando.value = false;
  });
}
function cancelar() {
  // NO muy elegante porque este botón está fuera de nuestro componente pero es la forma
  // que tiene bootstrap 3 de poder ocultar el panel sin mucho lío
  const btn = document.getElementById('btn-anadir');
  if (btn) {
    btn.click();
  }
}

watch(rutaId, () => {
  variacion.value.turno_nuevo_id = null;
  variacion.value.parada_id = null;
  paradas.value = [];
  transporte.turnos.get(
    { params: { ruta_id: rutaId.value, fecha: fechaInicio.value } },
  ).then((result) => {
    turnos.value = result.data;
  });
});

watch(range, (val) => {
  variacion.value.fecha_inicio = val.start;
  variacion.value.fecha_fin = val.end;
});

watch(() => variacion.value.tipo, (val) => {
  if (val === 'cambio_parada') {
    cargarParadas(props.turnoId);
  }

  if (!turnos.value.length && val === 'cambio_turno') {
    transporte.rutas.get().then((result) => {
      rutas.value = result.data;
    });
  }
});

watch(() => variacion.value.turno_nuevo_id, (val) => {
  cargarParadas(val);
});

// Si cambian selección de fechas, reiniciamos los días preseleccionados
watch(masDeUnaSemana, () => {
  seleccionarDias.value = false;
  diasSemanaSeleccionados.value = clone(props.diasSemana);
});
</script>

<template>
  <div class="row">
    <form
      ref="formVariacion"
      method="post"
      class="clearfix"
      @submit.prevent="guardar"
    >
      <InputCsrf />
      <div class="col-md-6 text-center">
        <input-fecha-hora
          v-model="range"
          :inline="true"
          :range="true"
          :unselectable="false"
        />
      </div>
      <div class="col-md-6">
        <div v-if="!range">
          <p class="text-info">
            <i class="fa fa-info-circle"></i>
            {{ $I18n.t('seleccione_rango_fechas') }}
          </p>
        </div>
        <div v-if="range" class="row">
          <div class="form-group col-md-6">
            <label>Desde</label>
            <p class="form-control">
              {{ fechaInicio }}
            </p>
            <input
              type="hidden"
              name="fecha_inicio"
              :value="fechaInicio"
            />
            <input-error v-model="variacion.fecha_inicio" :error="errores.fecha_inicio" />
          </div>
          <div class="form-group col-md-6">
            <label>Hasta</label>
            <p class="form-control">
              {{ fechaFin }}
            </p>
            <input
              type="hidden"
              name="fecha_fin"
              :value="fechaFin"
            />
            <input-error v-model="variacion.fecha_fin" :error="errores.fecha_fin" />
          </div>
        </div>
        <div v-if="fechaFin" class="form-group">
          <label>Tipo de variación</label>
          <select
            v-model="variacion.tipo"
            name="tipo"
            class="form-control"
            required
          >
            <option
              v-for="option in tipos"
              :key="option.id"
              :value="option.id"
            >
              {{ option.nombre }}
            </option>
          </select>
          <input-error v-model="variacion.tipo" :error="errores.tipo" />
        </div>
        <div v-if="variacion.tipo == 'cambio_turno'" class="row animated fadeInDown">
          <div class="col-md-6">
            <div class="form-group">
              <label>Ruta</label>
              <select
                v-model="rutaId"
                name="ruta_id"
                class="form-control"
              >
                <option
                  v-for="option in rutas"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.nombre }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>Turno</label>
              <select
                v-model="variacion.turno_nuevo_id"
                name="turno_nuevo_id"
                class="form-control"
              >
                <option
                  v-for="option in turnos"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.nombre }}
                </option>
              </select>
              <input-error v-model="variacion.turno_nuevo_id" :error="errores.turno_nuevo_id" />
            </div>
          </div>
        </div>
        <div v-if="variacion.tipo != 'ausencia'" class="form-group animated fadeIn">
          <label>{{ $t('parada') }}</label>
          <select
            v-model="variacion.parada_id"
            name="parada_id"
            class="form-control"
          >
            <option
              v-for="option in paradas"
              :key="option.id"
              :value="option.id"
            >
              {{ option.nombre }}
            </option>
          </select>
          <input-error v-model="variacion.parada_id" :error="errores.parada_id" />
        </div>
        <div v-if="!seleccionarDias && variacion.tipo === 'cambio_parada' && masDeUnaSemana">
          <input-checkbox-boolean
            v-model="seleccionarDias"
            tipo="switch"
          >
            {{ $t('dias_semana_seleccionar') }}
          </input-checkbox-boolean>
        </div>
        <div v-if="seleccionarDias">
          <div class="form-group">
            <label>{{ $t('dias_semana') }}</label>
            <InputDiasSemana
              v-model="diasSemanaSeleccionados"
              :dias-semana="diasSemana"
            />
          </div>
        </div>
        <div v-if="variacion.tipo == 'cambio_turno'">
          <selector-asiento
            v-model="variacion.asiento"
            :turno-id="variacion.turno_nuevo_id"
            :fecha-inicio="variacion.fecha_inicio"
            :fecha-fin="variacion.fecha_fin"
          >
          </selector-asiento>
          <input-error v-model="variacion.asiento" :error="errores.asiento" />
        </div>
        <div class="form-actions">
          <button
            type="button"
            class="btn btn-default"
            data-dismiss="modal"
            @click="cancelar"
          >
            {{ $I18n.t('Cancelar') }}
          </button>
          <boton-guardar :submitting="guardando" :disabled="!puedeGuardar"></boton-guardar>
        </div>
      </div>
    </form>
  </div>
</template>
